import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { FiCalendar, FiPlus, FiTrash } from "react-icons/fi";
import propTypes from "prop-types";
import styles from "./UpsellsBuilder.module.css";
import DropDownField, {
  IDropDownListItem,
} from "../../editable/EditableField/fields/DropDownField";
import MoneyInputField from "../../editable/EditableField/fields/MoneyInputField";
import NumberInputField from "../../editable/EditableField/fields/NumberInputField";
import TextInputField from "../../editable/EditableField/fields/TextInputField";
import DateField from "../../editable/EditableField/fields/DateField";
import SwitchField from "../../editable/EditableField/fields/SwitchField";

import {
  api,
  Product,
  ProductRate,
  ProductTypes,
  UpsellPackage,
  UpsellPackagedProduct,
} from "../../../logic/api";

import flex from "../../../styles/flex";

import ProductPickerDialog from "../../pickers/productPicker/ProductPickerDialog";

import ErrorDialog from "../../notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../notifications/progressDialog/progressDialog";

import { formatCurrency } from "../../../utility/utils";
import { ISessionState, SessionContext } from "../../../utility/useSession";
import { useDate, isValidDate } from "../../../utility/useDate";
import TabbedViewPage from "../../lists/TabbedView/TabbedViewPage";
import TabbedView from "../../lists/TabbedView/TabbedView";
import PhotoPicker from "../PhotoPicker/PhotoPicker";
import { useTranslation } from "react-i18next";

/*
export enum UpsellsAvailability {
    available = 0,
    NotYetAvailable = 1,
    NoLongerAvailable = 2,
}*/

export const getUpsellProductPrice = (
  packagedProduct: UpsellPackagedProduct
) => {
  if (packagedProduct.rateType == ProductRate.standard) {
    return packagedProduct.product.stdRate * packagedProduct.qty;
  } else if (packagedProduct.rateType == ProductRate.adjusted) {
    return packagedProduct.rate * packagedProduct.qty;
  }
  return 0;
};

export const getUpsellProductPriceFormatted = (
  packagedProduct: UpsellPackagedProduct,
  packagedProducts: UpsellPackagedProduct[],
  session: ISessionState
) => {
  const subProducts = packagedProducts.filter(
    (p) => p.topLevel == false && p.parentRef == packagedProduct.ref
  );
  const total = subProducts.reduce(
    (acc, cur) => acc + getUpsellProductPrice(cur),
    getUpsellProductPrice(packagedProduct)
  );
  return formatCurrency(total, session);
};

const UpsellBuilder: React.FunctionComponent<{
  upsellPackageId: number | null;
  show: boolean;
  onClose: (didUpdate: boolean) => void;
}> = (props) => {
  const session = useContext(SessionContext);
  const { localizeDate } = useDate();
  const { t } = useTranslation();
  const fetchUpsellPackage = api.hooks.upsell.get();
  const saveUpsellPackage = api.hooks.upsell.save();

  const defaultStartDate = new Date();
  const localDefaultStartDate = localizeDate(defaultStartDate).toISO();

  const [showDialog, setShowDialog] = useState(false);

  const [packageId, setPackageId] = useState<number | null>(null);
  const [packagedProducts, setPackagedProducts] = useState<
    UpsellPackagedProduct[]
  >([]);
  const [packageName, setPackageName] = useState("");
  //const [packageCode, setPackageCode] = useState("");
  const [packageRef, setPackageRef] = useState("");
  const [advertText, setAdvertText] = useState("");
  const [mobilePhotoFileName, setMobilePhotoFileName] = useState("");
  const [kioskPhotoFileName, setKioskPhotoFileName] = useState("");
  const [tabIndex, setTabIndex] = useState(0);

  const [startDate, setStartDate] = useState(localDefaultStartDate);
  const [endDate, setEndDate] = useState("");
  const [expires, setOfferExpires] = useState(false);
  const [active, setActive] = useState(true);
  const [preBookOnly, setPreBookOnly] = useState(false);
  const [pickingOffset, setPickingOffset] = useState<number | null>(null);

  const [err, setErr] = useState<Error | null>(null);
  const [groupRef, setGroupRef] = useState<string | undefined>(undefined);

  // const isPickingOffsetReleased = useReleaseManagement(ReleaseFlag.releasePickingOffset);

  const topLevelProducts = packagedProducts.filter((p) => p.topLevel == true);

  useEffect(() => {
    const propPackageId = props.upsellPackageId;
    if (propPackageId == null) {
      setPackageId(propPackageId);
      setPackageName("");
      // setPackageCode("");
      setPackageRef("");
      setAdvertText("");
      setMobilePhotoFileName("");
      setKioskPhotoFileName("");
      setStartDate(localDefaultStartDate);
      setEndDate("");
      setActive(true);
      setPreBookOnly(false);
      setOfferExpires(false);
      setPackagedProducts([]);
      setShowDialog(true);
    } else {
      setPackageId(propPackageId);
      setPackageName("");
      fetchUpsellPackage.send(session.current.context, {
        id: propPackageId,
      });
    }
  }, [props.upsellPackageId, props.show]);

  useEffect(() => {
    if (fetchUpsellPackage.data != null) {
      setPackageName(fetchUpsellPackage.data.name);
      setStartDate(
        fetchUpsellPackage.data.startDate == null
          ? ""
          : localizeDate(new Date(fetchUpsellPackage.data.startDate)).toISO() ?? ""
      );
      setEndDate(
        fetchUpsellPackage.data.endDate == null
          ? ""
          : localizeDate(new Date(fetchUpsellPackage.data.endDate)).toISO() ?? ""
      );
      setActive(fetchUpsellPackage.data.active);
      setPreBookOnly(fetchUpsellPackage.data.preBookOnly);
      setOfferExpires(fetchUpsellPackage.data.expires == true);
      // setPackageCode((fetchUpsellPackage.data.code || "").toString());
      setPackageRef((fetchUpsellPackage.data.ref || "").toString());
      setPackagedProducts([...fetchUpsellPackage.data.products]);
      setPickingOffset(fetchUpsellPackage.data.pickingOffset || 0);
      setAdvertText(fetchUpsellPackage.data.advertText);
      setMobilePhotoFileName(fetchUpsellPackage.data.mobilePhotoFileName);
      setKioskPhotoFileName(fetchUpsellPackage.data.kioskPhotoFileName);
      setShowDialog(true);
    }
  }, [fetchUpsellPackage.data]);

  const generateUpsellPackageCode = () => {
    const productTypes = [
      ProductTypes.valet,
      ProductTypes.collection,
      ProductTypes.delivery,
      ProductTypes.valet,
      ProductTypes.electricVehicle,
      ProductTypes.vip,
    ];
    const codeFragments: string[] = [
      packageId == null ? "#" : (packageId.toString(16).padStart(2, "0")).toUpperCase(),
      ...packagedProducts
        .filter(
          (p) =>
            p != null &&
            productTypes.includes(p.product.productType) &&
            p.product.code != null &&
            p.product.code.length > 0
        )
        .map((p) => p.product.code),
    ];
    return `UP_${codeFragments.join("-")}`;
  };

  const [showProductPickerDialog, setShowProductPickerDialog] = useState(false);

  const removePackagedProduct = (packagedProduct: UpsellPackagedProduct) => {
    const subProducts = packagedProducts.filter(
      (sp) => sp.parentRef == packagedProduct.ref && sp.topLevel == false
    );
    const indx = packagedProducts.indexOf(packagedProduct);
    if (indx != -1) {
      const prods = [...packagedProducts];

      if (packagedProduct.topLevel == true && subProducts.length > 0) {
        subProducts[0].topLevel = true;
        subProducts[0].ref = packagedProduct.ref;
        subProducts[0].parentRef = "";

        const subIndex = packagedProducts.indexOf(subProducts[0]);

        prods[indx] = subProducts[0];
        prods.splice(subIndex, 1);
      } else {
        prods.splice(indx, 1);
      }

      setPackagedProducts(prods);
    }
  };

  const save = async () => {
    const prods = packagedProducts.filter((p) => {
      return p.id != -1 && p.product != null;
    });

    const inActiveProducts = prods.find((p) => {
      return p.product.active != true;
    });
    if (inActiveProducts != null) {
      setErr(
        new Error(
          t("packages.inactiveProds")
        )
      );
      return;
    }
    const pkgCode = generateUpsellPackageCode();
    //setPackageCode(pkgCode);

    const pkg: UpsellPackage = {
      id: packageId,
      name: packageName,
      code: pkgCode,
      ref: packageRef,
      startDate: (startDate ?? "").toString().split("T")[0],
      endDate: isValidDate(endDate) ? endDate.split("T")[0] : null,
      products: prods,
      expires: expires,
      active: active,
      preBookOnly: preBookOnly,
      pickingOffset: pickingOffset ?? undefined,
      advertText: advertText,
      frontEndPhotoFileName: "", //Reserved for future use.
      kioskPhotoFileName: kioskPhotoFileName,
      mobilePhotoFileName: mobilePhotoFileName,
    };

    saveUpsellPackage.send(session.current.context, pkg, (result) => {
      if (result != null) {
        props.onClose(true);
      }
    });
  };

  const addProductToBooking = (newProduct: Product | null) => {
    setShowProductPickerDialog(false);
    if (newProduct == null) return;

    const prods = [...packagedProducts];

    if (
      groupRef != null &&
      newProduct.productType != ProductTypes.default
    ) {
      //Prevent products of the same type being added multiple times as a sub product. We do allow the default product types to be added multiple times.
      //Top level products are allowed since only one of these can ever be sold as part of an upsell. They act like groups.
      const topLevelProducts = prods.filter(
        (tp) => tp.topLevel == true && tp.ref == groupRef
      );

      for (const prod of topLevelProducts) {
        const hasSubProductsMatchingNewProductId = prods.some(
          (tp) =>
            tp.topLevel == false &&
            tp.parentRef == prod.ref &&
            tp.product.productType == newProduct.productType
        );
        if (hasSubProductsMatchingNewProductId) {
          setErr(

            new Error(t("upsells.multipleProdsSameType", {
              type: ProductTypes[
                newProduct.productType
              ].toString()
              , name: newProduct.name
            }))
          );
          return;
        }
      }
    }

    prods.push({
      product: newProduct,
      qty: 1,
      topLevel: groupRef == null,
      ref: new Date().getTime().toString(),
      parentRef: groupRef,
      rate: newProduct.stdRate,
      rateType: ProductRate.adjusted,
    });

    setGroupRef(undefined);
    setPackagedProducts(prods);
  }

  return (
    <Fragment>
      <ProgressDialog
        status={saveUpsellPackage.status}
        delay={500}
      ></ProgressDialog>
      <ProgressDialog
        status={fetchUpsellPackage.status}
        showOnIdle={props.show == true && showDialog == false}
        delay={500}
      ></ProgressDialog>
      <ErrorDialog
        err={fetchUpsellPackage.err}
        onClose={() => {
          props.onClose(false);
        }}
      ></ErrorDialog>
      <ProductPickerDialog
        title={t("upsells.addProdToUpsell")}
        show={showProductPickerDialog}
        onClose={(newProduct) => addProductToBooking(newProduct)}
      ></ProductPickerDialog>
      <Dialog
        open={props.show == true && showDialog == true}
        fullWidth={true}
        maxWidth={"lg"}
      >
        <DialogTitle>
          <b>
            {packageName.length == 0 ? t("upsells.upsellsPackageBuilder") : packageName}
          </b>
        </DialogTitle>

        <DialogContent>
          <ErrorDialog err={saveUpsellPackage.err}></ErrorDialog>
          <ErrorDialog err={err}></ErrorDialog>

          <TabbedView
            animated={false}
            selectedIndex={tabIndex}
            onSelectedIndexChange={(index) => setTabIndex(index)}
          >
            <TabbedViewPage title={t("upsells.upsellAdvert")}>
              <div style={{ minHeight: "50vh" }}>
                <div style={flex.flexRows}>
                  <div style={{ width: 300 }}>
                    <div style={flex.flexRows}>
                      <div style={flex.flexFill}>
                        <div style={{ paddingRight: 10 }}>
                          <div style={{ marginBottom: 15 }}>
                            <DateField
                              label={t("packages.availableFrom")}
                              value={startDate}
                              onChange={(val) => {
                                setStartDate(val);
                              }}
                            />
                          </div>
                          {expires == true && (
                            <div style={{ marginBottom: 15 }}>
                              <DateField
                                label={t("packages.availableUntil")}
                                value={endDate}
                                onChange={(val) => {
                                  setEndDate(val);
                                }}
                              />
                            </div>
                          )}

                          <div style={{ marginBottom: 10 }}>
                            <SwitchField
                              label={t("packages.availabilityExpires")}
                              value={expires}
                              onChange={(val) => {
                                setOfferExpires(val == true);
                              }}
                            />
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <SwitchField
                              label={t("packages.packageActive")}
                              value={active}
                              onChange={(val) => {
                                setActive(val == true);
                              }}
                            />
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <SwitchField
                              label={t("packages.prebookOnly")}
                              value={preBookOnly}
                              onChange={(val) => {
                                setPreBookOnly(val == true);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={flex.flexFill}>
                    <div>
                      <TextInputField
                        label={t("upsells.packageName")}
                        value={packageName}
                        onChange={(val) => {
                          setPackageName(val);
                        }}
                      ></TextInputField>

                      <div style={{ marginBottom: 10 }}></div>

                      <TextInputField
                        multiLine={true}
                        maxWidth={"100%"}
                        label={t("upsells.advert")}
                        value={advertText}
                        onChange={(val) => {
                          setAdvertText(val);
                        }}
                      ></TextInputField>

                      <div style={flex.flexRows}>
                        <div style={flex.flexAuto}>
                          <h2>{t("upsells.kioskAdvert")}</h2>

                          <PhotoPicker
                            scale={0.5}
                            width={
                              session.current.site?.preferences
                                .upsell_kiosk_img_w ?? 600
                            }
                            height={
                              session.current.site?.preferences
                                .upsell_kiosk_img_h ?? 800
                            }
                            fileName={kioskPhotoFileName}
                            onUpdated={(fileName) => {
                              setKioskPhotoFileName(fileName);
                            }}
                          ></PhotoPicker>
                        </div>
                        <div style={flex.flexAuto}>
                          <div style={{ width: 20 }}></div>
                        </div>
                        <div style={flex.flexAuto}>
                          <h2>{t("upsells.mobileAdvert")}</h2>

                          <PhotoPicker
                            scale={0.6}
                            width={
                              session.current.site?.preferences
                                .upsell_mobile_img_w ?? 400
                            }
                            height={
                              session.current.site?.preferences
                                .upsell_mobile_img_h ?? 600
                            }
                            fileName={mobilePhotoFileName}
                            onUpdated={(fileName) => {
                              setMobilePhotoFileName(fileName);
                            }}
                          ></PhotoPicker>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabbedViewPage>
            <TabbedViewPage title={t("booking.labels.products")}>
              <div style={{ minHeight: "50vh" }}>
                <div style={flex.flexRows}>
                  <div style={flex.flexFill}>
                    <div>
                      {topLevelProducts.map(
                        (topLevelProduct, topLevelProductIndex) => {
                          const subProducts = packagedProducts.filter(
                            (pp) =>
                              pp.parentRef == topLevelProduct.ref &&
                              pp.topLevel == false
                          );

                          const packagedProductListItems = (
                            packagedProductsForGroup: UpsellPackagedProduct[],
                            isGroup: boolean,
                            hasChildren: boolean
                          ) => {
                            return packagedProductsForGroup.map(
                              (packagedProduct, i) => {
                                const index =
                                  packagedProducts.indexOf(packagedProduct);
                                if (packagedProduct.product == null) {
                                  return <Fragment key={index}></Fragment>;
                                }

                                const styleList = [styles.upsellListItems];
                                if (i == 0) styleList.push(styles.upsellListItemsFirst);
                                if (i == packagedProductsForGroup.length - 1) styleList.push(styles.upsellListItemsLast);


                                return (
                                  <div key={index} className={styleList.join(" ")}>
                                    <div style={flex.flexRows}>
                                      <div style={flex.flexAuto}>
                                        <div style={{ paddingRight: 10 }}>
                                          <div>
                                            {packagedProduct.id != -1 && (
                                              <IconButton
                                                onClick={() => {
                                                  removePackagedProduct(
                                                    packagedProduct
                                                  );
                                                }}
                                              >
                                                <FiTrash />
                                              </IconButton>
                                            )}
                                            {packagedProduct.id == -1 && (
                                              <IconButton title={t("general.delete")} disabled={true}>
                                                <FiCalendar />
                                              </IconButton>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div
                                        style={
                                          (packagedProduct.topLevel == true &&
                                            isGroup) == false
                                            ? flex.flexFill
                                            : flex.flexAuto
                                        }
                                      >
                                        <div>
                                          {(packagedProduct.topLevel == true &&
                                            isGroup) == false && (
                                              <div style={{ paddingTop: 15 }}>
                                                <b>
                                                  {packagedProduct.alias !=
                                                    null && isGroup == true
                                                    ? packagedProduct.alias
                                                    : packagedProduct.product
                                                      .name}
                                                </b>{" "}
                                                {packagedProduct.product.active !=
                                                  true && (
                                                    <b style={{ color: "red" }}>
                                                      {t("packages.productNotActive")}
                                                    </b>
                                                  )}
                                              </div>
                                            )}

                                          {packagedProduct.topLevel == true &&
                                            isGroup && (
                                              <div
                                                style={{
                                                  paddingTop: 5,
                                                  paddingRight: 5,
                                                }}
                                              >
                                                <TextInputField
                                                  maxWidth={300}
                                                  label={t("reports.alias")}
                                                  value={
                                                    packagedProduct.alias ??
                                                    packagedProduct.product.name
                                                  }
                                                  onChange={(value) => {
                                                    packagedProduct.alias =
                                                      value;
                                                    setPackagedProducts([
                                                      ...packagedProducts,
                                                    ]);
                                                  }}
                                                ></TextInputField>
                                              </div>
                                            )}
                                        </div>
                                      </div>

                                      {packagedProduct.topLevel == true &&
                                        isGroup &&
                                        hasChildren == false && (
                                          <div style={flex.flexFill}>
                                            <div
                                              style={{
                                                paddingTop: 15,
                                                paddingLeft: 20,
                                              }}
                                            >
                                              <b>
                                                {packagedProduct.product.name}
                                              </b>
                                            </div>
                                          </div>
                                        )}

                                      {packagedProduct.topLevel == true &&
                                        isGroup == true &&
                                        hasChildren == true && (
                                          <div style={flex.flexFill}>
                                            <div
                                              style={{
                                                paddingTop: 15,
                                                paddingLeft: 20,
                                              }}
                                            >
                                              <b style={{ color: "orange" }}>
                                                {t("upsells.productGroup")}
                                              </b>
                                            </div>
                                          </div>
                                        )}

                                      {packagedProduct.product.maxQty != 0 &&
                                        (isGroup == false ||
                                          (isGroup == true &&
                                            hasChildren == false)) && (
                                          <div style={flex.flexAuto}>
                                            <div
                                              style={{
                                                paddingTop: 5,
                                                paddingRight: 5,
                                              }}
                                            >
                                              <NumberInputField
                                                maxWidth={70}
                                                label={t("payments.qty")}
                                                value={packagedProduct.qty}
                                                onChange={(value) => {
                                                  if (value <= 0) {
                                                    value = 1;
                                                  }
                                                  const prods = [
                                                    ...packagedProducts,
                                                  ];
                                                  prods[index] = {
                                                    ...packagedProduct,
                                                  };
                                                  prods[index].qty = value;
                                                  setPackagedProducts(prods);
                                                }}
                                              ></NumberInputField>
                                            </div>
                                          </div>
                                        )}

                                      {(isGroup == false ||
                                        (isGroup == true &&
                                          hasChildren == false)) && (
                                          <div style={flex.flexAuto}>
                                            <div
                                              style={{
                                                paddingTop: 5,
                                                paddingRight: 5,
                                              }}
                                            >
                                              <DropDownField
                                                label={t("packages.rateType")}
                                                minWidth={150}
                                                selectedItem={
                                                  {
                                                    value:
                                                      packagedProduct.rateType.toString(),
                                                  } as IDropDownListItem
                                                }
                                                onRequestData={async () => {
                                                  const items = [
                                                    {
                                                      name: t("packages.standard"),
                                                      value:
                                                        ProductRate.standard.toString(),
                                                    },
                                                    {
                                                      name: t("packages.adjusted"),
                                                      value:
                                                        ProductRate.adjusted.toString(),
                                                    },
                                                  ];
                                                  if (packagedProduct.id != -1) {
                                                    items.push({
                                                      name: t("packages.complimentary"),
                                                      value:
                                                        ProductRate.compDiscount.toString(),
                                                    });
                                                  }
                                                  return items;
                                                }}
                                                onChange={(selectedItem) => {
                                                  const prods = [
                                                    ...packagedProducts,
                                                  ];
                                                  prods[index] = {
                                                    ...packagedProduct,
                                                  };
                                                  prods[index].rateType =
                                                    parseInt(
                                                      selectedItem.value || ""
                                                    );
                                                  setPackagedProducts(prods);
                                                }}
                                              ></DropDownField>
                                            </div>
                                          </div>
                                        )}
                                      {(isGroup == false ||
                                        (isGroup == true &&
                                          hasChildren == false)) && (
                                          <div style={flex.flexAuto}>
                                            <div
                                              style={{
                                                paddingTop: 5,
                                                paddingRight: 5,
                                              }}
                                            >
                                              <MoneyInputField
                                                readOnly={
                                                  packagedProduct.rateType !=
                                                  ProductRate.adjusted
                                                }
                                                maxWidth={200}
                                                label={t("upsells.rateIncTax", {
                                                  tax: formatCurrency(
                                                    packagedProduct.product.vat,
                                                    session
                                                  )
                                                })}
                                                value={
                                                  packagedProduct.rateType ==
                                                    ProductRate.compDiscount
                                                    ? 0
                                                    : packagedProduct.rateType ==
                                                      ProductRate.standard
                                                      ? parseFloat(
                                                        packagedProduct.product.stdRate.toString()
                                                      )
                                                      : parseFloat(
                                                        packagedProduct.rate.toString()
                                                      )
                                                }
                                                onChange={(value) => {
                                                  const prods = [
                                                    ...packagedProducts,
                                                  ];
                                                  prods[index] = {
                                                    ...packagedProduct,
                                                  };
                                                  prods[index].rate = value;
                                                  setPackagedProducts(prods);
                                                }}
                                              ></MoneyInputField>
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                );
                              }
                            );
                          };
                          const top = packagedProductListItems(
                            [topLevelProduct],
                            true,
                            subProducts.length > 0
                          );
                          const children = packagedProductListItems(
                            subProducts.length == 0
                              ? []
                              : [topLevelProduct, ...subProducts],
                            false,
                            false
                          );
                          return (
                            <div
                              key={topLevelProductIndex}
                              style={{ paddingBottom: 10 }}
                            >


                              {top}


                              <div style={{ marginLeft: 50, marginTop: 5 }}>
                                {children.length > 0 && (
                                  <div style={{ paddingBottom: 5 }}>
                                    <i>{t("upsells.incProds")}</i>
                                  </div>
                                )}


                                {children}

                                <div style={flex.flexRows}>
                                  <div style={flex.flexFill}>
                                    <Button
                                      onClick={() => {
                                        setGroupRef(topLevelProduct.ref);
                                        setShowProductPickerDialog(true);
                                      }}
                                    >
                                      <FiPlus
                                        style={{ marginRight: 7 }}
                                      ></FiPlus>
                                      <b style={{ color: "blue" }}>
                                        {t("upsells.addProdGroup")}
                                      </b>
                                    </Button>
                                  </div>

                                  <div style={flex.flexAuto}>
                                    {children.length > 0 && (
                                      <div style={{ padding: 10 }}>
                                        {t("upsells.totalIncTax")}{" "}
                                        <b>
                                          {getUpsellProductPriceFormatted(
                                            topLevelProduct,
                                            packagedProducts,
                                            session
                                          )}
                                        </b>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </TabbedViewPage>
          </TabbedView>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              props.onClose(false);
            }}
          >
            {t("general.close")}
          </Button>

          <Button
            disabled={
              packageName.length == 0 ||
              advertText.length == 0 ||
              packagedProducts.length == 0
            }
            onClick={() => save()}
          >
            <b>{t("packages.savePackage")}</b>
          </Button>
          <Button
            onClick={() => {
              setGroupRef(undefined);
              setTabIndex(1);
              setShowProductPickerDialog(true);
            }}
          >
            <b>{t("input.labels.addProduct")}</b>
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};
UpsellBuilder.propTypes = {
  upsellPackageId: propTypes.any,
  show: propTypes.bool.isRequired,
  onClose: propTypes.func.isRequired,
};
export default UpsellBuilder;
